"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SignalDriveCharacteristicsDenormalizedSchema = void 0;
exports.attachDriveModesToSignals = attachDriveModesToSignals;
exports.convertSignalDriveCharacteristicsDenormalizedToSignalDriveMode = convertSignalDriveCharacteristicsDenormalizedToSignalDriveMode;
exports.convertSignalDriveModeToSignalDriveCharacteristicsDenormalized = convertSignalDriveModeToSignalDriveCharacteristicsDenormalized;
const zod_1 = require("zod");
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
exports.SignalDriveCharacteristicsDenormalizedSchema = zod_1.z
    .object({
    signalId: zod_1.z.string().describe('The id of the signal'),
    conditionId: zod_1.z
        .string()
        .nullable()
        .describe('The id of the condition under which voltage highs/lows are reported, if applicable.'),
    inputType: zod_1.z
        .nativeEnum(sigma_engine_shared_1.SignalInputDriveType)
        .nullable()
        .describe('The input type of the signal. Set to `null` if the signal is not an input.'),
    outputType: zod_1.z
        .nativeEnum(sigma_engine_shared_1.SignalOutputDriveType)
        .nullable()
        .describe('The output type of the signal. Set to `null` if the signal is not an output.'),
    outputHighVoltage: zod_1.z
        .string()
        .nullable()
        .describe('The high voltage level of the output in SI units - e.g. VOH. Leave blank if the signal is not an output.'),
    outputLowVoltage: zod_1.z
        .string()
        .nullable()
        .describe('The low voltage level of the output in SI units - e.g. VOL. Leave blank if the signal is not an output.'),
    inputHighVoltage: zod_1.z
        .string()
        .nullable()
        .describe('The high voltage level of the input in SI units - e.g. VIH. Leave blank if the signal is not an input.'),
    inputLowVoltage: zod_1.z
        .string()
        .nullable()
        .describe('The low voltage level of the input in SI units - e.g. VIL. Leave blank if the signal is not an input.'),
    internalPullupBiasImpedance: zod_1.z
        .string()
        .nullable()
        .describe('The Impedance of the internal pullup in SI units. Set to null if the signal does not have and internal pullup.'),
    internalPullupBiasVoltageReference: zod_1.z
        .string()
        .nullable()
        .describe('The voltage level in SI units the internal pullup connects to when its a specific voltage (e.g., "1.9V", "3.3V"). Set to null if no internal pullup or if it connects to a signal rather than a fixed voltage.'),
    internalPullupBiasSignalReference: zod_1.z
        .string()
        .nullable()
        .describe('The name of the signal the internal pullup is biased by (e.g., "VCC", "VDD"). Set to null if no internal pullup or if it connects to a fixed voltage rather than a signal.'),
    internalPulldownBiasImpedance: zod_1.z
        .string()
        .nullable()
        .describe('The Impedance of the internal pulldown in SI units. Set to null if the signal does not have and internal pulldown.'),
    internalPulldownBiasVoltageReference: zod_1.z
        .string()
        .nullable()
        .describe('The voltage level in SI units the internal pulldown connects to when its a specific voltage (e.g., "1.9V", "3.3V"). Set to null if no internal pulldown or if it connects to a signal rather than a fixed voltage.'),
    internalPulldownBiasSignalReference: zod_1.z
        .string()
        .nullable()
        .describe('The name of the signal the internal pulldown is biased by (e.g., "VCC", "VDD"). Set to null if no internal pulldown or if it connects to a fixed voltage rather than a signal.'),
    outputImpedance: zod_1.z
        .string()
        .nullable()
        .describe('The output impedance of the signal in SI units. Leave blank if the signal is not an output.'),
})
    .strict();
function attachDriveModesToSignals(signals, driveCharacteristics, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
    return signals.map((signal) => {
        const supportedDriveModes = [];
        for (const driveCharacteristic of driveCharacteristics.filter((char) => char.signalId === signal.id)) {
            try {
                supportedDriveModes.push(convertSignalDriveCharacteristicsDenormalizedToSignalDriveMode(driveCharacteristic));
            }
            catch (error) {
                logger.warn('AttachDriveModesToSignals_SkippedDriveCharacteristic', {
                    signalId: signal.id,
                    signalName: signal.name,
                    error: error instanceof Error ? error.message : String(error),
                });
            }
        }
        return supportedDriveModes.length > 0 ? { ...signal, supportedDriveModes } : signal;
    });
}
function convertSignalDriveCharacteristicsDenormalizedToSignalDriveMode(characteristics, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
    const driveType = characteristics.inputType ?? characteristics.outputType;
    if (!driveType) {
        throw new Error(`Signal ${characteristics.signalId} has neither input nor output type specified`);
    }
    const parameters = {};
    if (characteristics.outputHighVoltage !== null) {
        parameters.outputHighVoltage = characteristics.outputHighVoltage;
    }
    if (characteristics.outputLowVoltage !== null) {
        parameters.outputLowVoltage = characteristics.outputLowVoltage;
    }
    if (characteristics.inputHighVoltage !== null) {
        parameters.inputHighVoltage = characteristics.inputHighVoltage;
    }
    if (characteristics.inputLowVoltage !== null) {
        parameters.inputLowVoltage = characteristics.inputLowVoltage;
    }
    if (characteristics.outputImpedance !== null) {
        parameters.outputImpedance = characteristics.outputImpedance;
    }
    if (characteristics.internalPullupBiasImpedance !== null) {
        const pullupBias = characteristics.internalPullupBiasSignalReference !== null ?
            {
                type: 'signal',
                impedance: characteristics.internalPullupBiasImpedance,
                signalReference: { signal_name: characteristics.internalPullupBiasSignalReference },
            }
            : {
                type: 'voltage',
                impedance: characteristics.internalPullupBiasImpedance,
                voltageReference: sigma_engine_shared_1.Utils.parseNumericalSIValue(characteristics.internalPullupBiasVoltageReference ?? '0', sigma_engine_shared_1.CommonParameterUnits.voltage) ?? parseFloat(characteristics.internalPullupBiasVoltageReference ?? '0'),
            };
        parameters.internalPullupBias = pullupBias;
    }
    if (characteristics.internalPulldownBiasImpedance !== null) {
        const pulldownBias = characteristics.internalPulldownBiasSignalReference !== null ?
            {
                type: 'signal',
                impedance: characteristics.internalPulldownBiasImpedance,
                signalReference: { signal_name: characteristics.internalPulldownBiasSignalReference },
            }
            : {
                type: 'voltage',
                impedance: characteristics.internalPulldownBiasImpedance,
                voltageReference: sigma_engine_shared_1.Utils.parseNumericalSIValue(characteristics.internalPulldownBiasVoltageReference ?? '0', sigma_engine_shared_1.CommonParameterUnits.voltage) ?? parseFloat(characteristics.internalPulldownBiasVoltageReference ?? '0'),
            };
        parameters.internalPulldownBias = pulldownBias;
    }
    const conditions = [];
    if (characteristics.conditionId !== null) {
        conditions.push({
            condition: {
                mode: characteristics.conditionId,
                mode_state: true,
            },
        });
    }
    const signalDriveMode = {
        type: driveType,
    };
    if (Object.keys(parameters).length > 0) {
        signalDriveMode.parameters = parameters;
    }
    if (conditions.length > 0) {
        signalDriveMode.conditions = conditions;
    }
    const parseResult = sigma_engine_shared_1.DesignModels.SignalDriveModeDefinitionSchema.safeParse(signalDriveMode);
    if (!parseResult.success) {
        logger.warn('ConvertSignalDriveCharacteristicsDenormalized_InvalidFieldsDropped', {
            signalId: characteristics.signalId,
            errors: parseResult.error.flatten().fieldErrors,
        });
        return { type: driveType };
    }
    return parseResult.data;
}
function convertSignalDriveModeToSignalDriveCharacteristicsDenormalized(driveMode, signalId) {
    const parameters = driveMode.parameters ?? {};
    const isInputType = Object.values(sigma_engine_shared_1.SignalInputDriveType).includes(driveMode.type);
    const isOutputType = Object.values(sigma_engine_shared_1.SignalOutputDriveType).includes(driveMode.type);
    const conditionId = driveMode.conditions?.[0]?.condition && 'mode' in driveMode.conditions[0].condition ?
        driveMode.conditions[0].condition.mode
        : null;
    const convertToStringOrNull = (value) => {
        if (value === undefined || value === null) {
            return null;
        }
        return typeof value === 'string' ? value : value.toString();
    };
    const pullupBias = parameters.internalPullupBias;
    const pulldownBias = parameters.internalPulldownBias;
    return {
        signalId,
        conditionId,
        inputType: isInputType ? driveMode.type : null,
        outputType: isOutputType ? driveMode.type : null,
        outputHighVoltage: convertToStringOrNull(parameters.outputHighVoltage),
        outputLowVoltage: convertToStringOrNull(parameters.outputLowVoltage),
        inputHighVoltage: convertToStringOrNull(parameters.inputHighVoltage),
        inputLowVoltage: convertToStringOrNull(parameters.inputLowVoltage),
        internalPullupBiasImpedance: pullupBias?.impedance ?? null,
        internalPullupBiasVoltageReference: pullupBias?.type === 'voltage' ? pullupBias.voltageReference.toString() : null,
        internalPullupBiasSignalReference: pullupBias?.type === 'signal' ?
            Array.isArray(pullupBias.signalReference.signal_name) ?
                (pullupBias.signalReference.signal_name[0] ?? null)
                : (pullupBias.signalReference.signal_name ?? null)
            : null,
        internalPulldownBiasImpedance: pulldownBias?.impedance ?? null,
        internalPulldownBiasVoltageReference: pulldownBias?.type === 'voltage' ? pulldownBias.voltageReference.toString() : null,
        internalPulldownBiasSignalReference: pulldownBias?.type === 'signal' ?
            Array.isArray(pulldownBias.signalReference.signal_name) ?
                (pulldownBias.signalReference.signal_name[0] ?? null)
                : (pulldownBias.signalReference.signal_name ?? null)
            : null,
        outputImpedance: parameters.outputImpedance ?? null,
    };
}
//# sourceMappingURL=signal-drive-characteristics-denormalized.js.map